/**
 * Claimflow configurable component registrations.
 *
 * Called once at module bootstrap (see `../routes.tsx`). Each call to
 * `registerClaimflowComponents()` registers every configurable component
 * owned by the claimflow module with the shared component registry, so
 * the admin config page, CMD/Ctrl+click handler, and the
 * `withComponentRules` HOC (Phase 6) can look them up by id.
 *
 * This file intentionally does **not** import or modify the wrapped
 * component source — it only consumes the already-built React component
 * and describes its prop shape via a Zod schema. The HOC wiring happens
 * in Phase 6; Phase 3 only introduces the registry plumbing.
 *
 * Idempotency: calling `registerClaimflowComponents()` more than once in
 * the same process (e.g., Vite HMR, test re-imports) must not throw.
 * We guard each individual `registerComponent` call with
 * `isComponentRegistered()` so a partial registration still completes
 * on a subsequent invocation.
 */

import { z } from 'zod';

import {
  isComponentRegistered,
  registerComponent,
  withComponentRules,
} from '@components/shared/configurable';

import type {
  VeosClassementOption,
  VeosDocumentRow,
  VeosMailAttachment,
} from '../api/documentsService';
import type {
  VeosGuaranteeToClose,
  VeosGuaranteeToOpen,
  VeosTaskInfo,
} from '../api/taskService';
import {
  configContextSchema,
  dashboardContextSchema,
  taskDetailContextSchema,
} from '../contextVariables';
import type { ClaimSummary, CostLine } from '../types';
import { ClaimSummaryCard } from './ClaimSummaryCard';
import { ClaimTasksCard } from './ClaimTasksCard';
import { ConfigBackupsCard } from './config/ConfigBackupsCard';
import { ConfigHistoryCard } from './config/ConfigHistoryCard';
import { CloseCoverageCard } from './CloseCoverageCard';
import { CloseTaskCard } from './CloseTaskCard';
import { ClotureSinistreCard } from './ClotureSinistreCard';
import { ComptabiliteCard } from './ComptabiliteCard';
import { CostSynthesisCard } from './CostSynthesisCard';
import { CreateTaskCard } from './CreateTaskCard';
import { DocumentsCard } from './DocumentsCard';
import {
  DocumentPreview,
  DocumentPreviewConfigurable,
} from './documents/DocumentPreview';
import {
  EmailView,
  EmailViewConfigurable,
  type EmailViewModel,
} from './documents/EmailView';
import { EmailClassementForm } from './documents/DocumentClassementForm';
import { EditDocumentCard } from './EditDocumentCard';
import { EditIntervenantCard } from './EditIntervenantCard';
import { EmailCard } from './EmailCard';
import { EstimationCard } from './EstimationCard';
import { EstimationRecoursCard } from './EstimationRecoursCard';
import { IntervenantForm } from './IntervenantForm';
import { IntervenantsCard } from './IntervenantsCard';
import { MiseAJourDossierCard } from './MiseAJourDossierCard';
import { MissingPiecesCard } from './MissingPiecesCard';
import { OpenCoverageCard } from './OpenCoverageCard';
import { OtherActionsPanel } from './OtherActionsPanel';
import { OuvertureSinistreCard } from './OuvertureSinistreCard';
import { RecoveryCard } from './RecoveryCard';
import { RequestPiecesCard } from './RequestPiecesCard';
import { SettlementCard } from './SettlementCard';
import { TaskListCard } from './TaskListCard';

// ---------------------------------------------------------------------------
// OpenCoverageCard schema
// ---------------------------------------------------------------------------

/**
 * The prop shape the registry knows about for `OpenCoverageCard`.
 *
 * Mirrors `OpenCoverageCardProps` from `./OpenCoverageCard.tsx` and the
 * `VeosGuaranteeToOpen` payload in `../api/taskService.ts`. Keep the two
 * in sync when the card's props evolve — this schema is the contract the
 * translate endpoint uses to validate which prop names NL rules may
 * reference. The `onCoverageOpened` runtime callback is intentionally
 * omitted — like `CloseCoverageCard.onCoverageClosed` /
 * `EstimationCard.onProvisionSaved` it is wired by the page
 * (`TaskDetailPage` → refresh EstimationCard + `refetch()`), not a
 * tenant-tunable prop, so it has no place in the rule registry shape.
 */
const openCoverageCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  guarantees: z.array(
    z.object({
      CD_GAR: z.string(),
      LIB_GAR: z.string(),
    }),
  ),
}) satisfies z.ZodType<OpenCoverageCardRegistrationProps>;

/**
 * The props shape tracked by the registry for `OpenCoverageCard`.
 *
 * We declare an explicit alias so the Zod schema can be verified with
 * `satisfies z.ZodType<...>` at compile time — if the card's interface
 * drifts, TypeScript fails the build before the rule engine sees bad
 * data.
 */
interface OpenCoverageCardRegistrationProps {
  ses: string;
  idSin: string;
  guarantees: VeosGuaranteeToOpen[];
}

// ---------------------------------------------------------------------------
// TaskListCard schema
// ---------------------------------------------------------------------------

/**
 * `TaskListCard` takes zero props — it reads its data from
 * `useDataList(createTaskListConfig())` internally. The registry still
 * needs a schema entry so the admin config page, introspection-based
 * prop-label editor, and the Phase 6 HOC can treat it uniformly with
 * every other configurable component.
 *
 * Zero-prop components exercise `hide`, `show`, `disable`, and
 * `requireProp` effects without `setProp` or `filterPropValues`. The
 * Phase 9 work item explicitly validates that `z.object({})` does not
 * break any shared-code path downstream.
 */
type TaskListCardRegistrationProps = Record<string, never>;

const taskListCardPropsSchema = z.object(
  {},
) satisfies z.ZodType<TaskListCardRegistrationProps>;

// ---------------------------------------------------------------------------
// ClaimSummaryCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `ClaimSummaryCard`.
 *
 * Mirrors `ClaimSummaryCardProps` from `./ClaimSummaryCard.tsx`. The
 * nested `ClaimSummary` payload is modelled conservatively — every
 * field declared on the TypeScript interface is mirrored here so the
 * `satisfies z.ZodType<...>` check stays sound, but the schema is not
 * used to validate wire data; only rule-prop introspection relies on it.
 */
interface ClaimSummaryCardRegistrationProps {
  claim: ClaimSummary;
}

const claimSummarySchema = z.object({
  client: z.string(),
  dossierNumber: z.string(),
  date: z.string(),
  openingDate: z.string(),
  status: z.string(),
  materialManager: z.string(),
  bodilyManager: z.string(),
  plate: z.string(),
  location: z.string(),
  responsibility: z.string(),
  type: z.string(),
  article: z.string(),
  collisionType: z.string(),
  company: z.string(),
  damageNature: z.string(),
  thirdParty: z.string(),
  description: z.string(),
}) satisfies z.ZodType<ClaimSummary>;

const claimSummaryCardPropsSchema = z.object({
  claim: claimSummarySchema,
}) satisfies z.ZodType<ClaimSummaryCardRegistrationProps>;

// ---------------------------------------------------------------------------
// ClaimTasksCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `ClaimTasksCard` (mahhp 22862).
 *
 * Mirrors `ClaimTasksCardProps` from `./ClaimTasksCard.tsx`. The card is
 * self-fetching: it loads the claim's tasks (`WS2_GS_TAB_TACHES_SINISTRE` via
 * `taskService.fetchClaimTasks`) from the `idSin` it receives, with the task
 * open on the screen (`taskId`, optional — forwarded as `id_event`, its row is
 * highlighted) and the Veos session (`ses`, optional — carried in the
 * navigation state when a row opens another task). Its refetch signal
 * (`useClaimDataStore().tasksVersion`) and the close call are internal, not
 * props.
 *
 * Layout-rule surface (NOT a prop on this schema): `_layoutOverrides` carries
 * `columns` — stable keys `statut` / `date` / `libelle` / `categorie` /
 * `destinataire` / `action` (visible) and `classementLib` /
 * `libelleTypeTache` / `idEvent` (hidden by default, revealed with
 * `showColumn`) — `actions` (the per-row `close` "Clôturer" button) and
 * `title`; all published with the card's base layout config.
 */
interface ClaimTasksCardRegistrationProps {
  idSin: string;
  taskId?: string;
  ses?: string;
}

const claimTasksCardPropsSchema = z.object({
  idSin: z.string(),
  taskId: z.string().optional(),
  ses: z.string().optional(),
}) satisfies z.ZodType<ClaimTasksCardRegistrationProps>;

// ---------------------------------------------------------------------------
// CloseCoverageCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `CloseCoverageCard`.
 *
 * Mirrors `CloseCoverageCardProps` from `./CloseCoverageCard.tsx`. The
 * guarantees array mirrors the `VeosGuaranteeToClose` payload from
 * `../api/taskService.ts`.
 */
interface CloseCoverageCardRegistrationProps {
  ses: string;
  idSin: string;
  guarantees: VeosGuaranteeToClose[];
}

/**
 * The `onCoverageClosed` runtime callback (`CloseCoverageCardProps`) is
 * intentionally omitted from this schema — like `EstimationCard.onProvisionSaved`
 * / `SettlementCard.onSettlementCreated` it is wired by the page
 * (`TaskDetailPage` → `refetch()`), not a tenant-tunable prop, so it has no
 * place in the rule registry shape.
 */
const closeCoverageCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  guarantees: z.array(
    z.object({
      CD_GAR: z.string(),
      LIB_GAR: z.string(),
    }),
  ),
}) satisfies z.ZodType<CloseCoverageCardRegistrationProps>;

// ---------------------------------------------------------------------------
// CloseTaskCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `CloseTaskCard`.
 *
 * Mirrors `CloseTaskCardProps` from `./CloseTaskCard.tsx`. `taskInfo`
 * may be null when the upstream `fetchTaskInfo` call returns no match,
 * so the schema uses `.nullable()` to keep the `satisfies` assertion
 * compiling. `idSin` (the claim feeding the "Statut / motif" select) is
 * optional: without it the card closes the task with no motif field.
 * `taskInfo` is a `WS2_GS_SEL_TACHE` row (mahhp 22943): `CLASSEMENT` /
 * `CLASSEMENT_LIB` are null on a task with no classification.
 */
interface CloseTaskCardRegistrationProps {
  taskId: string;
  taskInfo: VeosTaskInfo | null;
  idSin?: string;
}

const veosTaskInfoSchema = z.object({
  ID_EVENT: z.union([z.number(), z.string()]),
  ID_SIN: z.union([z.number(), z.string()]).optional(),
  DATE_DEBUT: z.union([z.string(), z.number()]),
  PRENOM_PER: z.string(),
  NOM_PER: z.string(),
  ECRAN: z.string(),
  CLASSEMENT: z.string().nullable().optional(),
  CLASSEMENT_LIB: z.string().nullable().optional(),
  LIBELLE_TYPE_TACHE: z.string().optional(),
  STATUT: z.string().optional(),
}) satisfies z.ZodType<VeosTaskInfo>;

const closeTaskCardPropsSchema = z.object({
  taskId: z.string(),
  taskInfo: veosTaskInfoSchema.nullable(),
  idSin: z.string().optional(),
}) satisfies z.ZodType<CloseTaskCardRegistrationProps>;

// ---------------------------------------------------------------------------
// CostSynthesisCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `CostSynthesisCard`.
 *
 * Mirrors `CostSynthesisCardProps` from `./CostSynthesisCard.tsx`.
 * `CostLine` amounts are numeric-or-null to match the domain model; `isTotal`
 * / `poste` split each garantie's main row from its poste rows (mahhp 22854).
 * `defaultShowPostes` is the tenant-tunable initial state of the "Postes"
 * header toggle (`setProp`); `undefined` ⇒ off (garantie rows only).
 */
interface CostSynthesisCardRegistrationProps {
  costs: CostLine[];
  totalCost: number;
  defaultShowPostes?: boolean;
}

const costLineSchema = z.object({
  guarantee: z.string(),
  provision: z.number().nullable(),
  settlement: z.number().nullable(),
  grossCharge: z.number().nullable(),
  forecast: z.number().nullable(),
  recourse: z.number().nullable(),
  cost: z.number().nullable(),
  isTotal: z.boolean(),
  poste: z.string(),
}) satisfies z.ZodType<CostLine>;

const costSynthesisCardPropsSchema = z.object({
  costs: z.array(costLineSchema),
  totalCost: z.number(),
  defaultShowPostes: z.boolean().optional(),
}) satisfies z.ZodType<CostSynthesisCardRegistrationProps>;

// ---------------------------------------------------------------------------
// ComptabiliteCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `ComptabiliteCard` (mahhp 22853).
 *
 * Mirrors `ComptabiliteCardProps` from `./ComptabiliteCard.tsx`. The card is
 * self-fetching and read-only: it loads the claim's accounting movements
 * (`WS2_GS_TAB_COMPTABILITE` via `comptabiliteService`) from the `idSin` it
 * receives, so the only props the registry tracks are the two ids (`ses` is
 * optional — the fetch does not need it).
 *
 * Layout-rule surface (NOT a prop on this schema): `_layoutOverrides` carries
 * `columns` — stable keys `date` / `sens` / `categ` / `beneficiaire` /
 * `libelle` / `montant` / `util` (visible) and `idSrg` / `idrBor` / `idBor` /
 * `dateLchq` / `datgenLchq` / `datoOper` / `datoOper2` (hidden by default,
 * revealed with `showColumn`) — plus `title`; all published with the card's
 * base layout config.
 */
interface ComptabiliteCardRegistrationProps {
  idSin: string;
  ses?: string;
}

const comptabiliteCardPropsSchema = z.object({
  idSin: z.string(),
  ses: z.string().optional(),
}) satisfies z.ZodType<ComptabiliteCardRegistrationProps>;

// ---------------------------------------------------------------------------
// CreateTaskCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `CreateTaskCard`.
 *
 * Mirrors `CreateTaskCardProps` from `./CreateTaskCard.tsx`.
 */
interface CreateTaskCardRegistrationProps {
  ses: string;
  idSin: string;
  ncbtSin: string;
}

const createTaskCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  ncbtSin: z.string(),
}) satisfies z.ZodType<CreateTaskCardRegistrationProps>;

// ---------------------------------------------------------------------------
// DocumentsCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `DocumentsCard`.
 *
 * Mirrors `DocumentsCardProps` from `./DocumentsCard.tsx`. The card is
 * self-fetching: it loads the claim's documents (and category options) from
 * `documentsService` using the `ses` / `idSin` it receives, so the only props
 * the registry tracks are those two ids. The optional UI-context props
 * (`idEvent`, the popout-header metadata, `hidePopOut` / `inPopoutWindow` —
 * set by the host page / the popout window) and the injected
 * `_layoutOverrides` meta-prop are not tenant-tunable and are intentionally
 * omitted. The header filters (Prod | Sin → `tab_adoc`, category → `cd_ds`,
 * mahhp 22227) are internal card state, not props; so is the e-mail
 * "Répondre" dispatch (mahhp 22227 — the card asks the claim view's Email card
 * through `useClaimDataStore`, or, in the popout window, over the popout
 * BroadcastChannel).
 *
 * Layout-rule surface (NOT a prop on this schema): `_layoutOverrides` carries
 * `columns` (stable keys `kind` / `type` / `sens` / `libelle` / `category` /
 * `date` / `creator` / `statut` / `fileName`), `filters` (the header controls
 * `scope` / `category`) and `actions` (the header `popOut` "Ouvrir" button) —
 * all three are published with the card's base layout config.
 */
interface DocumentsCardRegistrationProps {
  ses: string;
  idSin: string;
}

const documentsCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
}) satisfies z.ZodType<DocumentsCardRegistrationProps>;

// ---------------------------------------------------------------------------
// DocumentPreview schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `DocumentPreview`.
 *
 * Mirrors `DocumentPreviewProps` from `./documents/DocumentPreview.tsx`. The
 * two data props are modelled conservatively: `row` mirrors `VeosDocumentRow`
 * and `classementOptions` mirrors `VeosClassementOption[]` (both from
 * `../api/documentsService`), like the `ClaimSummary` / `VeosTaskInfo` schemas
 * above.
 *
 * The runtime callback `onSaved` (the page-wired list-refresh) is intentionally
 * omitted — like `OpenCoverageCard.onCoverageOpened` it is wired by the host
 * (`DocumentsCard` → `refresh()`), not a tenant-tunable prop, so it has no place
 * in the rule registry shape.
 *
 * Layout-rule surface (NOT a prop on this schema): the component consumes an
 * injected `_layoutOverrides` meta-prop (the `withComponentRules` HOC supplies
 * it). Its single bucket is `fields`, keyed by the two stable field keys
 * (`classement`, `libelle`) that drive the autosave inputs' label / placeholder
 * / visibility / order — documented on the `_layoutOverrides` prop JSDoc in
 * `DocumentPreview.tsx`.
 */
interface DocumentPreviewRegistrationProps {
  row: VeosDocumentRow;
  classementOptions: VeosClassementOption[];
}

const veosDocumentRowSchema = z.object({
  API: z.string(),
  ID_ADOC: z.number(),
  LIB_ADOC: z.string(),
  CONTENT_TYPE: z.string(),
  DAT_ADOC: z.string(),
  TYPE_ADOC: z.string(),
  LIB_DS: z.string().nullable(),
  ACATEGORISER: z.number(),
  NOM_ADOC: z.string().nullable(),
  // Added for mahhp 22268 — optional/nullable (older rows omit them).
  NOM_PER_USER: z.string().nullable().optional(),
  SENS: z.string().nullable().optional(),
  DOC_TACHE: z.number().nullable().optional(),
}) satisfies z.ZodType<VeosDocumentRow>;

const veosClassementOptionSchema = z.object({
  CD_DS: z.string(),
  LIB_DS: z.string(),
}) satisfies z.ZodType<VeosClassementOption>;

const documentPreviewPropsSchema = z.object({
  row: veosDocumentRowSchema,
  classementOptions: z.array(veosClassementOptionSchema),
}) satisfies z.ZodType<DocumentPreviewRegistrationProps>;

// ---------------------------------------------------------------------------
// EmailClassementForm schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `EmailClassementForm` (the
 * "renommer les emails" classement/libellé form added for mahhp 22268). Same
 * two data props as `DocumentPreview`; the page-wired `onSaved` refresh is
 * omitted (host-owned, not tenant-tunable). The injected `_layoutOverrides`
 * meta-prop carries the `fields` bucket keyed by the two stable field keys
 * (`classement`, `libelle`).
 */
interface EmailClassementFormRegistrationProps {
  row: VeosDocumentRow;
  classementOptions: VeosClassementOption[];
}

const emailClassementFormPropsSchema = z.object({
  row: veosDocumentRowSchema,
  classementOptions: z.array(veosClassementOptionSchema),
}) satisfies z.ZodType<EmailClassementFormRegistrationProps>;

// ---------------------------------------------------------------------------
// EmailView schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `EmailView`.
 *
 * Mirrors `EmailViewProps` from `./documents/EmailView.tsx`. The nested
 * `email` payload mirrors `EmailViewModel` and `classementOptions` /
 * `documents` mirror `VeosClassementOption[]` / `VeosDocumentRow[]` (both from
 * `../api/documentsService`), modelled conservatively like the `ClaimSummary`
 * / `VeosDocumentRow` schemas above.
 *
 * The runtime callbacks `resolveAttachment` (the lazy `mail-pj-get` content
 * resolver, wired by `EmailDetail`), `onGedded` (the page-wired GED-refresh)
 * and `onReply` (the "Répondre" button, mahhp 22227 — wired by `EmailDetail`
 * for received GED e-mails only; the button renders only when it is set) are
 * intentionally omitted — like `DocumentPreview.onSaved` /
 * `OpenCoverageCard.onCoverageOpened` they are wired by the host, not
 * tenant-tunable props, so they have no place in the rule registry shape.
 *
 * Layout-rule surface (NOT a prop on this schema): the component consumes an
 * injected `_layoutOverrides` meta-prop (the `withComponentRules` HOC supplies
 * it). The header rows read `items` (falling back to `fields`), keyed by the
 * four stable header field keys (`from`, `to`, `date`, `subject`) — label /
 * visibility / order; the attachment GED controls read `fields`
 * (`classement`, `libelle`) and `actions.submit` ("Valider", mahhp 22843); the
 * header "Répondre" button reads `actions.reply` (label / visibility, mahhp
 * 22227). All documented on the `_layoutOverrides` prop JSDoc in
 * `EmailView.tsx`.
 */
interface EmailViewRegistrationProps {
  email: EmailViewModel;
  classementOptions: VeosClassementOption[];
  documents: VeosDocumentRow[];
  idSin: string;
  notice?: string;
}

const veosMailAttachmentSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  contentType: z.string(),
  data: z.string().nullable(),
  cleanContentType: z.string().optional(),
  size: z.number().nullable().optional(),
}) satisfies z.ZodType<VeosMailAttachment>;

const emailViewModelSchema = z.object({
  from: z.string(),
  to: z.string(),
  date: z.string(),
  subject: z.string(),
  body: z.string(),
  bodyIsHtml: z.boolean().optional(),
  attachments: z.array(veosMailAttachmentSchema),
}) satisfies z.ZodType<EmailViewModel>;

const emailViewPropsSchema = z.object({
  email: emailViewModelSchema,
  classementOptions: z.array(veosClassementOptionSchema),
  documents: z.array(veosDocumentRowSchema),
  idSin: z.string(),
  notice: z.string().optional(),
}) satisfies z.ZodType<EmailViewRegistrationProps>;

// ---------------------------------------------------------------------------
// IntervenantForm schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `IntervenantForm`.
 *
 * Mirrors `IntervenantFormProps` from `./IntervenantForm.tsx`.
 *
 * `allowedCategories` is the tenant-tunable allow-list of `CD_CATEG` codes
 * that constrains the Catégorie select options. The categories are fetched
 * internally (`boby-ws2-gs-sel-categ-sinper` → component state), not passed
 * as a prop, so `filterPropValues` cannot reach them — rule authors
 * `setProp` this allow-list instead (mirrors `SettlementCard.allowedNatures`
 * / `EstimationCard.deductionPostes`). It is `.optional()` because the
 * component applies no constraint when unset.
 *
 * `defaultCategory` (a `CD_CATEG` code) and `defaultExistant` seed the
 * form's initial `categ` / `existant` values. Together with the
 * `hideField existant` layout effect, `defaultExistant` is the **freeze
 * recipe**: hide the Existant checkbox and pin the creation mode —
 * `false` locks the card on the "new intervenant" path, `true` (the
 * literal fallback) on the "existing intervenant" path. Both are loosely
 * typed (`string` / `boolean`) so rule authors can point them at any
 * tenant's seeded category without a code change.
 *
 * `existantByCategory` maps a `CD_CATEG` code to the `existant` value to
 * apply when the handler selects that category — auto-(un)checks the
 * Existant checkbox at runtime for every category in one effect (a single
 * category can also be driven by a `setFieldCondition existant …
 * { checked }` "Cocher" / "Décocher" effect; the generic `setValue` stays
 * off the rule surface). It is a plain
 * `Record<string, boolean>` (an open key space, again so no code change is
 * needed per tenant category) and applies on the category TRANSITION only,
 * so the box stays togglable; pair it with a `setFieldCondition existant …
 * { disabled: true }` effect to lock it. It is `setProp`-only — never
 * listed in `filterableProps`, which targets inline data arrays.
 *
 * See `.claude-work/specs/intervenant-form.md` for the documented rule
 * JSONs.
 */
interface IntervenantFormRegistrationProps {
  ses: string;
  idSin: string;
  allowedCategories?: string[];
  defaultCategory?: string;
  defaultExistant?: boolean;
  existantByCategory?: Record<string, boolean>;
}

const intervenantFormPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  allowedCategories: z.array(z.string()).optional(),
  defaultCategory: z.string().optional(),
  defaultExistant: z.boolean().optional(),
  existantByCategory: z.record(z.string(), z.boolean()).optional(),
}) satisfies z.ZodType<IntervenantFormRegistrationProps>;

// ---------------------------------------------------------------------------
// EditIntervenantCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `EditIntervenantCard`
 * (« Modifier un intervenant », mahhp 22919).
 *
 * Mirrors `EditIntervenantCardProps` from `./EditIntervenantCard.tsx`. The card
 * is self-fetching: it loads the claim's intervenants (`WS2_GS_SEL_IDSINP`),
 * the selected one (`GET /sinper/{id_sinp}`) and the Compagnie options through
 * `intervenantService`, and saves through the `claimflow-update-stakeholder`
 * workflow — so the only props the registry tracks are the two ids. The
 * "edit this intervenant" requests (`useClaimDataStore().requestEditIntervenant`)
 * are store state, not props.
 *
 * Layout-rule surface (NOT a prop on this schema): the injected
 * `_layoutOverrides` meta-prop carries `fields` (stable keys `idSinp` / `nom` /
 * `prenom` / `email` / `telephone` / `adresse1` / `adresse2` / `cp` / `ville` /
 * `type` / `ref` / `cdCie` / `numPolTiers` / `numSinTiers` / `risque1`),
 * `actions` (`submit` = "Enregistrer les modifications") and `title` — all
 * published with the card's base EntityForm config.
 */
interface EditIntervenantCardRegistrationProps {
  ses: string;
  idSin: string;
}

const editIntervenantCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
}) satisfies z.ZodType<EditIntervenantCardRegistrationProps>;

// ---------------------------------------------------------------------------
// IntervenantsCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `IntervenantsCard`
 * (« Intervenants » table, mahhp 22538).
 *
 * Mirrors `IntervenantsCardProps` from `./IntervenantsCard.tsx`. The card is
 * self-fetching: it loads the claim's intervenants (`WS2_GS_TAB_INTERVENANTS`
 * via `intervenantService.fetchClaimIntervenants`, with the Veos session as
 * `SES`) from the two ids it receives. Its refetch signal
 * (`useClaimDataStore().intervenantsVersion`) and the pencil's edit request
 * (`requestEditIntervenant` + `focusCardAnchor`) are internal, not props. The
 * rows are fetched client-side, so the config-page preview renders the table
 * from a live fetch — they cannot be seeded through `previewProps` (typed `P`).
 *
 * Layout-rule surface (NOT a prop on this schema): `_layoutOverrides` carries
 * `columns` — stable keys `categ` / `num` / `nom` / `prenom` / `email` /
 * `tel` / `action` (visible) and `idPer` / `idSinp` (hidden by default,
 * revealed with `showColumn`) — `actions` (the per-row `edit` pencil) and
 * `title`; all published with the card's base layout config.
 */
interface IntervenantsCardRegistrationProps {
  idSin: string;
  ses: string;
}

const intervenantsCardPropsSchema = z.object({
  idSin: z.string(),
  ses: z.string(),
}) satisfies z.ZodType<IntervenantsCardRegistrationProps>;

// ---------------------------------------------------------------------------
// OuvertureSinistreCard schema
// ---------------------------------------------------------------------------

interface OuvertureSinistreCardRegistrationProps {
  ses: string;
  idSin?: string;
}

const ouvertureSinistreCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string().optional(),
}) satisfies z.ZodType<OuvertureSinistreCardRegistrationProps>;

// ---------------------------------------------------------------------------
// RequestPiecesCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `RequestPiecesCard`.
 *
 * Mirrors `RequestPiecesCardProps` from `./RequestPiecesCard.tsx`. The
 * card accepts either a string or numeric claim id, so the schema uses
 * `z.union([z.string(), z.number()])` to keep the `satisfies` assertion
 * compiling against the component's declared prop type.
 */
interface RequestPiecesCardRegistrationProps {
  idSin: string | number;
}

const requestPiecesCardPropsSchema = z.object({
  idSin: z.union([z.string(), z.number()]),
}) satisfies z.ZodType<RequestPiecesCardRegistrationProps>;

// ---------------------------------------------------------------------------
// MissingPiecesCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `MissingPiecesCard`.
 *
 * Mirrors `MissingPiecesCardProps` from `./MissingPiecesCard.tsx`. The
 * card accepts either a string or numeric claim id, so the schema uses
 * `z.union([z.string(), z.number()])` to keep the `satisfies` assertion
 * compiling against the component's declared prop type.
 */
interface MissingPiecesCardRegistrationProps {
  idSin: string | number;
}

const missingPiecesCardPropsSchema = z.object({
  idSin: z.union([z.string(), z.number()]),
}) satisfies z.ZodType<MissingPiecesCardRegistrationProps>;

// ---------------------------------------------------------------------------
// EditDocumentCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `EditDocumentCard`.
 *
 * Mirrors `EditDocumentCardProps` from `./EditDocumentCard.tsx`. The card
 * accepts a string-only `idSin` (the BoBy `id_sin` query param is forwarded
 * verbatim and the IGA `fusion-word` body stringifies the same value).
 */
interface EditDocumentCardRegistrationProps {
  idSin: string;
}

const editDocumentCardPropsSchema = z.object({
  idSin: z.string(),
}) satisfies z.ZodType<EditDocumentCardRegistrationProps>;

// ---------------------------------------------------------------------------
// SettlementCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `SettlementCard`.
 *
 * Mirrors `SettlementCardProps` from `./SettlementCard.tsx`. The four
 * `default*` props are tenant-overridable via `setProp` rules — each
 * default seeds the matching field on the card's form. `iga-servicing`
 * authors per-tenant rules to set them (`VIR`, `21965`, `EU`, `RGSINO`);
 * other tenants can copy/adapt the JSON shapes documented in
 * `.claude-work/specs/settlement-card.md`.
 *
 * The `default*` types are loosely typed as `string` because the IGA
 * dictionaries only ship a small fixed set today (`VIR` for mode reg,
 * `EU`/`US` for monnaie, `RGSINO` for nature, and the bank IDs mirror
 * `ID_RIB` integers serialised as strings) but rule authors must remain
 * free to point them at any future seeded value without a code change.
 *
 * `allowedNatures` is the tenant-tunable allow-list of `CD_OP` codes that
 * constrains the Nature opération select options. The cdops are fetched
 * internally (not a prop), so `filterPropValues` cannot reach them — rule
 * authors `setProp` this allow-list instead (mirrors
 * `EstimationCard.deductionPostes`). It is `.optional()` because the
 * component applies no constraint when unset.
 */
interface SettlementCardRegistrationProps {
  ses: string;
  idSin: string;
  defaultModeReglement?: string;
  defaultBanque?: string;
  defaultMonnaie?: string;
  defaultNature?: string;
  allowedNatures?: string[];
}

const settlementCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  defaultModeReglement: z.string().optional(),
  defaultBanque: z.string().optional(),
  defaultMonnaie: z.string().optional(),
  defaultNature: z.string().optional(),
  allowedNatures: z.array(z.string()).optional(),
}) satisfies z.ZodType<SettlementCardRegistrationProps>;

// ---------------------------------------------------------------------------
// RecoveryCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `RecoveryCard`.
 *
 * Mirrors `RecoveryCardProps` from `./RecoveryCard.tsx`. The four
 * `default*` props are tenant-overridable via `setProp` rules — each
 * default seeds the matching field on the card's form. `iga-servicing`
 * authors per-tenant rules to set them (`CHQ`, `21965`, `EU`, plus any
 * tenant-specific Nature opération); other tenants can copy/adapt the
 * JSON shapes documented in `.claude-work/specs/recovery-card.md`.
 *
 * `defaultNature` mirrors the `SettlementCard` prop: when unset, the
 * card late-binds the Nature opération from `paramReglement.CD_OP` once
 * the per-claim selector resolves. Tenants that need to pin a specific
 * `CD_OP` (e.g. an alternative recovery operation code) author a
 * `setProp` rule on this prop.
 *
 * `allowedNatures` is the tenant-tunable allow-list of `CD_OP` codes that
 * constrains the Nature opération select options. The cdops are fetched
 * internally (not a prop), so `filterPropValues` cannot reach them — rule
 * authors `setProp` this allow-list instead (mirrors
 * `EstimationCard.deductionPostes`). It is `.optional()` because the
 * component applies no constraint when unset.
 *
 * `allowedIntervenantCategories` is the same mechanism one level over:
 * a tenant-tunable allow-list of `CD_CATEG` codes that constrains the
 * Intervenant select options. The intervenants are self-fetched too, so
 * rule authors `setProp` the allow-list rather than `filterPropValues`.
 * Unset / empty ⇒ no constraint and the sinper selector keeps its
 * server-side `type: 'TIERS'` filter (byte-identical baseline). Non-empty
 * ⇒ the card fetches sinper untyped (so non-TIERS categories such as
 * `CIEADV` / `APPORTEUR` can come back at all) and filters the rows on
 * `CD_CATEG` before they reach both the options and the submit lookup.
 *
 * The `default*` types are loosely typed as `string` so rule authors can
 * point them at any future seeded value without a code change.
 */
interface RecoveryCardRegistrationProps {
  ses: string;
  idSin: string;
  defaultModeReglement?: string;
  defaultBanque?: string;
  defaultMonnaie?: string;
  defaultNature?: string;
  allowedNatures?: string[];
  allowedIntervenantCategories?: string[];
}

const recoveryCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  defaultModeReglement: z.string().optional(),
  defaultBanque: z.string().optional(),
  defaultMonnaie: z.string().optional(),
  defaultNature: z.string().optional(),
  allowedNatures: z.array(z.string()).optional(),
  allowedIntervenantCategories: z.array(z.string()).optional(),
}) satisfies z.ZodType<RecoveryCardRegistrationProps>;

// ---------------------------------------------------------------------------
// EmailCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `EmailCard`.
 *
 * Mirrors `EmailCardProps` from `./EmailCard.tsx`. Only `idSin` is
 * required — it drives every on-mount fetch (reply-to options, recipient
 * suggestions, attachable documents). The remaining props pre-seed form
 * fields (`dossierNumber` → the "Objet" default, `senderEmail` /
 * `senderName` → the "De" sender) and are therefore optional, matching
 * the component interface so the `satisfies` assertion compiles.
 *
 * `ses` and `ncbtSin` are accepted by the component for parity with the
 * other claimflow cards even though the current render path does not read
 * them; they stay optional here so rule authors can still target them.
 *
 * Layout-rule surfaces (NOT props on this schema): the card consumes an
 * injected `_layoutOverrides` meta-prop (the `withComponentRules` HOC supplies
 * it), exactly like the other configurable claimflow cards. It is deliberately
 * absent from `propsSchema` / `previewProps` / `defaultPropLabels` — those are
 * typed against the real component props. Its two override buckets are:
 *  - `fields` — the 8 stable email field keys (`replyTo`, `template`, `from`,
 *    `to`, `cc`, `attachments`, `subject`, `message`).
 *  - `columns` — the attachments table's stable column key (`label`, header
 *    "Document"), a rule target since Phase 3 (`migrate-email-card.md`).
 * Both are documented on the `_layoutOverrides` prop JSDoc in `EmailCard.tsx`.
 * The attachment rows are fetched client-side (`fetchAttachableDocuments`), not
 * passed as props, so the config-page preview renders the table from a live
 * fetch — they cannot be seeded through `previewProps` (which is typed `P`).
 */
interface EmailCardRegistrationProps {
  idSin: string;
  ses?: string;
  ncbtSin?: string;
  dossierNumber?: string;
  senderEmail?: string;
  senderName?: string;
}

const emailCardPropsSchema = z.object({
  idSin: z.string(),
  ses: z.string().optional(),
  ncbtSin: z.string().optional(),
  dossierNumber: z.string().optional(),
  senderEmail: z.string().optional(),
  senderName: z.string().optional(),
}) satisfies z.ZodType<EmailCardRegistrationProps>;

// ---------------------------------------------------------------------------
// MiseAJourDossierCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `MiseAJourDossierCard`.
 *
 * Mirrors `MiseAJourDossierCardProps` from `./MiseAJourDossierCard.tsx`.
 * The card is self-fetching: it loads the full claim flux and every ERP
 * dropdown from the veos proxy using the `ses` / `idSin` it receives, so
 * the only props the registry tracks are those two ids.
 */
interface MiseAJourDossierCardRegistrationProps {
  ses: string;
  idSin: string;
}

const miseAJourDossierCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
}) satisfies z.ZodType<MiseAJourDossierCardRegistrationProps>;

// ---------------------------------------------------------------------------
// ClotureSinistreCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `ClotureSinistreCard`
 * (« Clôture », mahhp 22846).
 *
 * Mirrors `ClotureSinistreCardProps` from `./ClotureSinistreCard.tsx`. The card
 * is self-fetching: it loads the closure motifs (`WS2_GS_SEL_MOTIF_CLOTURE`)
 * and closes the claim through the `claimflow-close-claim` workflow via
 * `clotureService`, so the only prop the registry tracks is the claim id. The
 * `onClaimClosed` runtime callback is intentionally omitted — like
 * `SettlementCard.onSettlementCreated` it is wired by the page
 * (`TaskDetailPage` → `refetch()`), not a tenant-tunable prop.
 *
 * Layout-rule surface (NOT a prop on this schema): the injected
 * `_layoutOverrides` meta-prop carries `fields` (stable key `motif`),
 * `actions` (`submit` = « Valider ») and `title` — all published with the
 * card's base EntityForm config.
 */
interface ClotureSinistreCardRegistrationProps {
  idSin: string;
}

const clotureSinistreCardPropsSchema = z.object({
  idSin: z.string(),
}) satisfies z.ZodType<ClotureSinistreCardRegistrationProps>;

// ---------------------------------------------------------------------------
// EstimationCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `EstimationCard`.
 *
 * Mirrors `EstimationCardProps` from `./EstimationCard.tsx`. The card is
 * self-fetching: it loads the garanties + estimations from the veos proxy
 * using the `ses` / `idSin` it receives. `deductionPostes` is the single
 * tenant-overridable prop — a list of `CD_PSD` codes whose amount is
 * subtracted from the net total and forced NEGATIVE end to end (Franchise +
 * Valeur de sauvetage, mahhp 22851). It is `.optional()` because the component
 * defaults it to `DEFAULT_DEDUCTION_POSTES` (`FRAN`, `SAUV`, `ASSFRC`,
 * `ERVSRC` — `./deductionPostes.ts`); rule authors `setProp` it per tenant (see
 * `.claude-work/specs/estimation-card.md`). `guaranteesToClose` is the sole
 * source of the Garantie select options (full replace) — it mirrors
 * `CloseCoverageCard.guarantees` and is fed from `TaskDetailPage`'s
 * `guaranteesToClose` state; it is `.optional()` because the component defaults
 * it to `[]`. The `onProvisionSaved` runtime callback is intentionally omitted —
 * like `SettlementCard.onSettlementCreated` it is wired by the page, not a
 * tunable prop. `refreshKey` is likewise omitted — it is a parent-driven
 * runtime refresh signal (bumped by `TaskDetailPage` to force a re-fetch), not
 * a tenant-configurable value.
 *
 * `ncbtSin` (mahhp 22858) is the claim reference forwarded as `ncbt_sin` to
 * the `claimflow-post-comment` workflow with the required Commentaire — claim
 * data passed by `TaskDetailPage` like `ses` / `idSin`, `.optional()` because
 * the workflow only reads it for @mention tasks (the card sends none). The
 * Commentaire itself is NOT a prop: it is the `commentaire` field of the
 * card's EntityForm config, so it is targeted through `_layoutOverrides.fields`
 * (rename / placeholder / hide …) like `garantie` / `perteTotale`.
 */
interface EstimationCardRegistrationProps {
  ses: string;
  idSin: string;
  ncbtSin?: string;
  deductionPostes?: string[];
  guaranteesToClose?: VeosGuaranteeToClose[];
}

const estimationCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  ncbtSin: z.string().optional(),
  deductionPostes: z.array(z.string()).optional(),
  guaranteesToClose: z
    .array(z.object({ CD_GAR: z.string(), LIB_GAR: z.string() }))
    .optional(),
}) satisfies z.ZodType<EstimationCardRegistrationProps>;

// ---------------------------------------------------------------------------
// EstimationRecoursCard schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `EstimationRecoursCard`
 * (« Estimation recours », mahhp 22442).
 *
 * Mirrors `EstimationRecoursCardProps` from `./EstimationRecoursCard.tsx` — the
 * recours twin of `EstimationCard`, so the same four tracked props. The card is
 * self-fetching: it loads the recours estimations from the veos proxy using the
 * `idSin` it receives (`ses` is carried for parity with the sibling cards but
 * the recours endpoints are session-less). `deductionPostes` is the
 * tenant-overridable list of `CD_PSD` codes whose amount is SUBTRACTED from the
 * « Recours estimé » total and forced NEGATIVE end to end (Franchise + Valeur
 * de sauvetage, mahhp 22851); it is `.optional()` because the component
 * defaults it to `DEFAULT_DEDUCTION_POSTES` (`./deductionPostes.ts`) and rule
 * authors `setProp` it per tenant. `guaranteesToClose` is the sole
 * source of the Garantie select options (full replace), fed from
 * `TaskDetailPage`'s `guaranteesToClose` state; `.optional()` because the
 * component defaults it to `[]`.
 *
 * The `onEstimationSaved` runtime callback is intentionally omitted — like
 * `EstimationCard.onProvisionSaved` it is wired by the page, not a tunable
 * prop. `refreshKey` is likewise omitted (parent-driven refresh signal), and so
 * is the injected `_layoutOverrides` meta-prop (buckets: `fields` keyed by
 * `garantie` / `commentaire`; `columns` keyed by `poste` / `montant`).
 *
 * `ncbtSin` (mahhp 22858) is the claim reference forwarded as `ncbt_sin` to
 * the `claimflow-post-comment` workflow with the required Commentaire — same
 * contract as `EstimationCard.ncbtSin`, `.optional()`.
 */
interface EstimationRecoursCardRegistrationProps {
  ses: string;
  idSin: string;
  ncbtSin?: string;
  deductionPostes?: string[];
  guaranteesToClose?: VeosGuaranteeToClose[];
}

const estimationRecoursCardPropsSchema = z.object({
  ses: z.string(),
  idSin: z.string(),
  ncbtSin: z.string().optional(),
  deductionPostes: z.array(z.string()).optional(),
  guaranteesToClose: z
    .array(z.object({ CD_GAR: z.string(), LIB_GAR: z.string() }))
    .optional(),
}) satisfies z.ZodType<EstimationRecoursCardRegistrationProps>;

// ---------------------------------------------------------------------------
// OtherActionsPanel schema
// ---------------------------------------------------------------------------

/**
 * The props shape tracked by the registry for `OtherActionsPanel`.
 *
 * Mirrors `OtherActionsPanelProps` from `./OtherActionsPanel.tsx`, minus the
 * runtime bits the registry never models: the `onSelect` callback is wired by
 * the page (focus / inject), not a tenant-tunable prop, so — like
 * `OpenCoverageCard.onCoverageOpened` — it is intentionally omitted from this
 * schema. The injected `_layoutOverrides` meta-prop (the `withComponentRules`
 * HOC supplies it) is likewise absent; its single bucket is `items`, keyed by
 * each action's stable `key`, driving the launcher rows' label / visibility /
 * order. Only the declarative `actions` list (stable `key` + display `label`)
 * is a real, rule-targetable prop.
 */
interface OtherActionsPanelRegistrationProps {
  actions: { key: string; label: string }[];
}

const otherActionsPanelPropsSchema = z.object({
  actions: z.array(z.object({ key: z.string(), label: z.string() })),
}) satisfies z.ZodType<OtherActionsPanelRegistrationProps>;

// ---------------------------------------------------------------------------
// ConfigHistoryCard / ConfigBackupsCard schemas
// ---------------------------------------------------------------------------

/**
 * The Config History / Backups cards are zero-prop, self-fetching content
 * components (like `TaskListCard`) — they read their data from the tenant
 * config endpoints internally and expose only the injected `_layoutOverrides`
 * meta-prop (not part of `propsSchema`). Registering them makes their DataList
 * columns tenant-configurable (rename / hide / reorder / tooltip) via the
 * layout-mutation rule family.
 */
type ConfigHistoryCardRegistrationProps = Record<string, never>;

const configHistoryCardPropsSchema = z.object(
  {},
) satisfies z.ZodType<ConfigHistoryCardRegistrationProps>;

type ConfigBackupsCardRegistrationProps = Record<string, never>;

const configBackupsCardPropsSchema = z.object(
  {},
) satisfies z.ZodType<ConfigBackupsCardRegistrationProps>;

// ---------------------------------------------------------------------------
// Idempotent bootstrap
// ---------------------------------------------------------------------------

/**
 * Registers every claimflow component with the configurable registry.
 *
 * Safe to call multiple times — existing registrations are skipped
 * via `isComponentRegistered()` so HMR and test re-imports don't trip
 * the double-registration guard in `registerComponent()`.
 */
export function registerClaimflowComponents(): void {
  if (!isComponentRegistered('claimflow.openCoverageCard')) {
    registerComponent<OpenCoverageCardRegistrationProps>({
      id: 'claimflow.openCoverageCard',
      displayName: 'Open Coverage Card',
      module: 'claimflow',
      component: OpenCoverageCard,
      propsSchema: openCoverageCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        guarantees: 'Guarantees',
      },
      // `guarantees` is a real inline data array (`VeosGuaranteeToOpen[]` of
      // `{ CD_GAR, LIB_GAR }`) the host passes in full — the only legitimate
      // `filterPropValues` target on this card.
      filterableProps: ['guarantees'],
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
        guarantees: [{ CD_GAR: 'RC', LIB_GAR: 'Responsabilité Civile' }],
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.taskListCard')) {
    registerComponent<TaskListCardRegistrationProps>({
      id: 'claimflow.taskListCard',
      displayName: 'Task List Card',
      module: 'claimflow',
      component: TaskListCard,
      propsSchema: taskListCardPropsSchema,
      defaultPropLabels: {},
      previewProps: {},
      contextVariables: dashboardContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.claimSummaryCard')) {
    registerComponent<ClaimSummaryCardRegistrationProps>({
      id: 'claimflow.claimSummaryCard',
      displayName: 'Claim Summary Card',
      module: 'claimflow',
      component: ClaimSummaryCard,
      propsSchema: claimSummaryCardPropsSchema,
      defaultPropLabels: {
        claim: 'Sinistre',
      },
      previewProps: {
        claim: {
          client: 'Demo Client',
          dossierNumber: 'DEMO-0001',
          date: '2026-01-01',
          openingDate: '2026-01-02',
          status: 'Ouvert',
          materialManager: 'Jean Dupont',
          bodilyManager: 'Marie Martin',
          plate: 'AB-123-CD',
          location: 'Paris',
          responsibility: '50%',
          type: 'Corporate',
          article: 'A1',
          collisionType: 'Frontal',
          company: 'Demo SA',
          damageNature: 'Matériel',
          thirdParty: 'Tiers Inconnu',
          description: 'Description de démonstration.',
        },
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.claimTasksCard')) {
    registerComponent<ClaimTasksCardRegistrationProps>({
      id: 'claimflow.claimTasksCard',
      displayName: 'Claim Tasks Card',
      module: 'claimflow',
      component: ClaimTasksCard,
      propsSchema: claimTasksCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
        taskId: 'Task ID',
        ses: 'Session ID',
      },
      previewProps: {
        idSin: 'demo-claim',
        taskId: 'demo-task',
        ses: 'demo-session',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.closeCoverageCard')) {
    registerComponent<CloseCoverageCardRegistrationProps>({
      id: 'claimflow.closeCoverageCard',
      displayName: 'Close Coverage Card',
      module: 'claimflow',
      component: CloseCoverageCard,
      propsSchema: closeCoverageCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        guarantees: 'Garanties à clôturer',
      },
      // `guarantees` is a real inline data array (`VeosGuaranteeToClose[]` of
      // `{ CD_GAR, LIB_GAR }`) the host passes in full — the only legitimate
      // `filterPropValues` target on this card.
      filterableProps: ['guarantees'],
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
        guarantees: [{ CD_GAR: 'RC', LIB_GAR: 'Responsabilité Civile' }],
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.closeTaskCard')) {
    registerComponent<CloseTaskCardRegistrationProps>({
      id: 'claimflow.closeTaskCard',
      displayName: 'Close Task Card',
      module: 'claimflow',
      component: CloseTaskCard,
      propsSchema: closeTaskCardPropsSchema,
      defaultPropLabels: {
        taskId: 'Tâche',
        taskInfo: 'Détail de la tâche',
        idSin: 'Claim ID',
      },
      previewProps: {
        taskId: 'demo-task',
        taskInfo: null,
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.costSynthesisCard')) {
    registerComponent<CostSynthesisCardRegistrationProps>({
      id: 'claimflow.costSynthesisCard',
      displayName: 'Cost Synthesis Card',
      module: 'claimflow',
      component: CostSynthesisCard,
      propsSchema: costSynthesisCardPropsSchema,
      defaultPropLabels: {
        costs: 'Lignes de coût',
        totalCost: 'Coût total',
        defaultShowPostes: 'Postes affichés par défaut',
      },
      previewProps: {
        costs: [
          {
            guarantee: 'RC',
            provision: 1000,
            settlement: null,
            grossCharge: 1000,
            forecast: null,
            recourse: null,
            cost: 1000,
            isTotal: true,
            poste: 'TOTAL',
          },
          {
            guarantee: 'RC',
            provision: 1000,
            settlement: null,
            grossCharge: 1000,
            forecast: null,
            recourse: null,
            cost: 1000,
            isTotal: false,
            poste: 'Dommages matériels',
          },
        ],
        totalCost: 1000,
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.comptabiliteCard')) {
    registerComponent<ComptabiliteCardRegistrationProps>({
      id: 'claimflow.comptabiliteCard',
      displayName: 'Comptabilité Card',
      module: 'claimflow',
      component: ComptabiliteCard,
      propsSchema: comptabiliteCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
        ses: 'Session ID',
      },
      previewProps: {
        idSin: 'demo-claim',
        ses: 'demo-session',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.createTaskCard')) {
    registerComponent<CreateTaskCardRegistrationProps>({
      id: 'claimflow.createTaskCard',
      displayName: 'Create Task Card',
      module: 'claimflow',
      component: CreateTaskCard,
      propsSchema: createTaskCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        ncbtSin: 'Référence sinistre',
      },
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
        ncbtSin: 'DEMO-0001',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.documentsCard')) {
    registerComponent<DocumentsCardRegistrationProps>({
      id: 'claimflow.documentsCard',
      displayName: 'Documents Card',
      module: 'claimflow',
      component: DocumentsCard,
      propsSchema: documentsCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
      },
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.documentPreview')) {
    registerComponent<DocumentPreviewRegistrationProps>({
      id: 'claimflow.documentPreview',
      displayName: 'Document Preview',
      module: 'claimflow',
      component: DocumentPreview,
      propsSchema: documentPreviewPropsSchema,
      defaultPropLabels: {
        row: 'Document',
        classementOptions: 'Catégories de classement',
      },
      previewProps: {
        row: {
          API: 'DOC',
          ID_ADOC: 1,
          LIB_ADOC: 'Document de démonstration',
          CONTENT_TYPE: 'application/pdf',
          DAT_ADOC: '2026-01-01',
          TYPE_ADOC: '01',
          LIB_DS: 'Rapport',
          ACATEGORISER: 0,
          NOM_ADOC: 'demo.pdf',
        },
        classementOptions: [{ CD_DS: '01', LIB_DS: 'Rapport' }],
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.emailView')) {
    registerComponent<EmailViewRegistrationProps>({
      id: 'claimflow.emailView',
      displayName: 'Email View',
      module: 'claimflow',
      component: EmailView,
      propsSchema: emailViewPropsSchema,
      defaultPropLabels: {
        email: 'E-mail',
        classementOptions: 'Catégories de classement',
        documents: 'Documents du dossier',
        idSin: 'Claim ID',
        notice: 'Bandeau d’information',
      },
      previewProps: {
        email: {
          from: 'Service Sinistres <sinistres@assurance-demo.fr>',
          to: 'client@example.com',
          date: '12/06/2026 10:00',
          subject: 'Votre dossier sinistre DEMO-0001',
          body: 'Bonjour,\n\nVeuillez trouver ci-joint les éléments demandés.\n\nCordialement.',
          attachments: [],
        },
        classementOptions: [{ CD_DS: '01', LIB_DS: 'Rapport' }],
        documents: [],
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.emailClassement')) {
    registerComponent<EmailClassementFormRegistrationProps>({
      id: 'claimflow.emailClassement',
      displayName: 'Email Classement',
      module: 'claimflow',
      component: EmailClassementForm,
      propsSchema: emailClassementFormPropsSchema,
      defaultPropLabels: {
        row: 'Document',
        classementOptions: 'Catégories de classement',
      },
      previewProps: {
        row: {
          API: 'EMAIL',
          ID_ADOC: 1,
          LIB_ADOC: 'Email de démonstration',
          CONTENT_TYPE: 'message/rfc822',
          DAT_ADOC: '2026-01-01',
          TYPE_ADOC: '01',
          LIB_DS: 'Correspondance',
          ACATEGORISER: 0,
          NOM_ADOC: 'demo.eml',
        },
        classementOptions: [{ CD_DS: '01', LIB_DS: 'Correspondance' }],
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.intervenantForm')) {
    registerComponent<IntervenantFormRegistrationProps>({
      id: 'claimflow.intervenantForm',
      displayName: 'Intervenant Form',
      module: 'claimflow',
      component: IntervenantForm,
      propsSchema: intervenantFormPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        allowedCategories: 'Catégories autorisées',
        defaultCategory: 'Catégorie par défaut',
        defaultExistant: 'Existant par défaut',
        existantByCategory: 'Existant selon la catégorie',
      },
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.editIntervenantCard')) {
    registerComponent<EditIntervenantCardRegistrationProps>({
      id: 'claimflow.editIntervenantCard',
      displayName: 'Edit Intervenant Card',
      module: 'claimflow',
      component: EditIntervenantCard,
      propsSchema: editIntervenantCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
      },
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.intervenantsCard')) {
    registerComponent<IntervenantsCardRegistrationProps>({
      id: 'claimflow.intervenantsCard',
      displayName: 'Intervenants Card',
      module: 'claimflow',
      component: IntervenantsCard,
      propsSchema: intervenantsCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
        ses: 'Session ID',
      },
      previewProps: {
        idSin: 'demo-claim',
        ses: 'demo-session',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.ouvertureSinistreCard')) {
    registerComponent<OuvertureSinistreCardRegistrationProps>({
      id: 'claimflow.ouvertureSinistreCard',
      displayName: 'Ouverture Sinistre Card',
      module: 'claimflow',
      component: OuvertureSinistreCard,
      propsSchema: ouvertureSinistreCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
      },
      previewProps: {
        ses: 'PREVIEW-SES',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.requestPiecesCard')) {
    registerComponent<RequestPiecesCardRegistrationProps>({
      id: 'claimflow.requestPiecesCard',
      displayName: 'Request Pieces Card',
      module: 'claimflow',
      component: RequestPiecesCard,
      propsSchema: requestPiecesCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
      },
      previewProps: {
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.missingPiecesCard')) {
    registerComponent<MissingPiecesCardRegistrationProps>({
      id: 'claimflow.missingPiecesCard',
      displayName: 'Missing Pieces Card',
      module: 'claimflow',
      component: MissingPiecesCard,
      propsSchema: missingPiecesCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
      },
      previewProps: {
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.editDocumentCard')) {
    registerComponent<EditDocumentCardRegistrationProps>({
      id: 'claimflow.editDocumentCard',
      displayName: 'Edit Document Card',
      module: 'claimflow',
      component: EditDocumentCard,
      propsSchema: editDocumentCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
      },
      previewProps: {
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.settlementCard')) {
    registerComponent<SettlementCardRegistrationProps>({
      id: 'claimflow.settlementCard',
      displayName: 'Settlement Card',
      module: 'claimflow',
      component: SettlementCard,
      propsSchema: settlementCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        defaultModeReglement: 'Mode de règlement par défaut',
        defaultBanque: 'Banque par défaut',
        defaultMonnaie: 'Monnaie par défaut',
        defaultNature: 'Nature opération par défaut',
        allowedNatures: 'Natures opération autorisées',
      },
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
        defaultModeReglement: 'VIR',
        defaultBanque: '21965',
        defaultMonnaie: 'EU',
        defaultNature: 'RGSINO',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.recoveryCard')) {
    registerComponent<RecoveryCardRegistrationProps>({
      id: 'claimflow.recoveryCard',
      displayName: 'Recovery Card',
      module: 'claimflow',
      component: RecoveryCard,
      propsSchema: recoveryCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        defaultModeReglement: 'Mode de règlement par défaut',
        defaultBanque: 'Banque par défaut',
        defaultMonnaie: 'Monnaie par défaut',
        defaultNature: 'Nature opération par défaut',
        allowedNatures: 'Natures opération autorisées',
        allowedIntervenantCategories: "Catégories d'intervenant autorisées",
      },
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
        defaultModeReglement: 'CHQ',
        defaultBanque: '21965',
        defaultMonnaie: 'EU',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.emailCard')) {
    registerComponent<EmailCardRegistrationProps>({
      id: 'claimflow.emailCard',
      displayName: 'Email Card',
      module: 'claimflow',
      component: EmailCard,
      propsSchema: emailCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
        ses: 'Session ID',
        ncbtSin: 'Référence sinistre',
        dossierNumber: 'Numéro de dossier',
        senderEmail: 'Email expéditeur',
        senderName: 'Nom expéditeur',
      },
      previewProps: {
        idSin: 'demo-claim',
        ses: 'demo-session',
        ncbtSin: 'DEMO-0001',
        dossierNumber: 'DEMO-0001',
        senderEmail: 'sinistres@assurance-demo.fr',
        senderName: 'Service Sinistres',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.miseAJourDossierCard')) {
    registerComponent<MiseAJourDossierCardRegistrationProps>({
      id: 'claimflow.miseAJourDossierCard',
      displayName: 'Mise À Jour Dossier Card',
      module: 'claimflow',
      component: MiseAJourDossierCard,
      propsSchema: miseAJourDossierCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
      },
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.clotureSinistreCard')) {
    registerComponent<ClotureSinistreCardRegistrationProps>({
      id: 'claimflow.clotureSinistreCard',
      displayName: 'Clôture Sinistre Card',
      module: 'claimflow',
      component: ClotureSinistreCard,
      propsSchema: clotureSinistreCardPropsSchema,
      defaultPropLabels: {
        idSin: 'Claim ID',
      },
      previewProps: {
        idSin: 'demo-claim',
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.estimationCard')) {
    registerComponent<EstimationCardRegistrationProps>({
      id: 'claimflow.estimationCard',
      displayName: 'Estimation Card',
      module: 'claimflow',
      component: EstimationCard,
      propsSchema: estimationCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        ncbtSin: 'Référence sinistre',
        deductionPostes: 'Postes en déduction',
        guaranteesToClose: 'Garanties à clôturer',
      },
      // `guaranteesToClose` is a real inline data array (`VeosGuaranteeToClose[]`
      // of `{ CD_GAR, LIB_GAR }`) the host passes from `TaskDetailPage` — it is
      // the sole source of the Garantie select options, so `filterPropValues`
      // legitimately narrows it (mirrors the coverage cards' `guarantees`).
      // `deductionPostes` is deliberately NOT filterable: it is an
      // allow-list constraint (default `DEFAULT_DEDUCTION_POSTES`) authored
      // via `setProp`.
      filterableProps: ['guaranteesToClose'],
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
        ncbtSin: 'DEMO-0001',
        deductionPostes: [],
        guaranteesToClose: [{ CD_GAR: 'RC', LIB_GAR: 'Responsabilité Civile' }],
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.estimationRecoursCard')) {
    registerComponent<EstimationRecoursCardRegistrationProps>({
      id: 'claimflow.estimationRecoursCard',
      displayName: 'Estimation Recours Card',
      module: 'claimflow',
      component: EstimationRecoursCard,
      propsSchema: estimationRecoursCardPropsSchema,
      defaultPropLabels: {
        ses: 'Session ID',
        idSin: 'Claim ID',
        ncbtSin: 'Référence sinistre',
        deductionPostes: 'Postes en déduction',
        guaranteesToClose: 'Garanties à clôturer',
      },
      // Same rationale as `claimflow.estimationCard`: `guaranteesToClose` is a
      // real inline data array (`VeosGuaranteeToClose[]` of
      // `{ CD_GAR, LIB_GAR }`) the host passes from `TaskDetailPage` and is the
      // sole source of the Garantie select options, so `filterPropValues`
      // legitimately narrows it. `deductionPostes` is deliberately NOT
      // filterable: it is an allow-list (default `DEFAULT_DEDUCTION_POSTES`)
      // authored via `setProp`.
      filterableProps: ['guaranteesToClose'],
      previewProps: {
        ses: 'demo-session',
        idSin: 'demo-claim',
        ncbtSin: 'DEMO-0001',
        deductionPostes: [],
        guaranteesToClose: [{ CD_GAR: 'RC', LIB_GAR: 'Responsabilité Civile' }],
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.otherActionsPanel')) {
    registerComponent<OtherActionsPanelRegistrationProps>({
      id: 'claimflow.otherActionsPanel',
      displayName: 'Autres actions',
      module: 'claimflow',
      component: OtherActionsPanel,
      propsSchema: otherActionsPanelPropsSchema,
      defaultPropLabels: {
        actions: 'Actions',
      },
      previewProps: {
        actions: [
          { key: 'email', label: 'Email' },
          { key: 'reglement', label: 'Règlement' },
          { key: 'creerTache', label: 'Créer une tâche' },
        ],
      },
      contextVariables: taskDetailContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.configHistoryCard')) {
    registerComponent<ConfigHistoryCardRegistrationProps>({
      id: 'claimflow.configHistoryCard',
      displayName: 'Config History Card',
      module: 'claimflow',
      component: ConfigHistoryCard,
      propsSchema: configHistoryCardPropsSchema,
      defaultPropLabels: {},
      previewProps: {},
      contextVariables: configContextSchema,
    });
  }

  if (!isComponentRegistered('claimflow.configBackupsCard')) {
    registerComponent<ConfigBackupsCardRegistrationProps>({
      id: 'claimflow.configBackupsCard',
      displayName: 'Config Backups Card',
      module: 'claimflow',
      component: ConfigBackupsCard,
      propsSchema: configBackupsCardPropsSchema,
      defaultPropLabels: {},
      previewProps: {},
      contextVariables: configContextSchema,
    });
  }
}

// ---------------------------------------------------------------------------
// Phase 7 — rule-aware wrapped exports
// ---------------------------------------------------------------------------

// Ensure the registry is populated at module load so any consumer that
// imports the wrapped component below also implicitly registers it.
// Keeping this eager matches the previous behaviour and means consumers
// do not need to remember to call `registerClaimflowComponents()`
// separately from the routes.tsx bootstrap.
registerClaimflowComponents();

/**
 * Rule-aware wrapped `OpenCoverageCard`.
 *
 * `TaskDetailPage` imports this constant (aliased as `OpenCoverageCard`)
 * so rules authored through the admin module edit page fire on every
 * render without any JSX churn on the page itself. See Phase 6 +
 * Phase 7 of `.claude-work/plans/component-rules-plan.md`.
 */
export const OpenCoverageCardConfigurable = withComponentRules(
  OpenCoverageCard,
  'claimflow.openCoverageCard',
);

/**
 * Rule-aware wrapped `TaskListCard`.
 *
 * `DashboardPage` imports this constant (aliased as `TaskListCard`) so
 * admin-authored rules fire on every render without any JSX churn on
 * the page itself. `TaskListCard` is a zero-prop component — the HOC
 * path exercised by rules on this registration is `hide` / `show` /
 * `disable` / `requireProp`, not `setProp` / `filterPropValues`. See
 * Phase 9 of `.claude-work/plans/component-rules-plan.md`.
 */
export const TaskListCardConfigurable = withComponentRules(
  TaskListCard,
  'claimflow.taskListCard',
);

/**
 * Rule-aware wrapped `ClaimSummaryCard`. Ships unused — pages keep
 * importing the raw component until a per-page swap is approved.
 */
export const ClaimSummaryCardConfigurable = withComponentRules(
  ClaimSummaryCard,
  'claimflow.claimSummaryCard',
);

/**
 * Rule-aware wrapped `ClaimTasksCard` (mahhp 22862). `TaskDetailPage` imports
 * it (aliased as `ClaimTasksCard`) and renders it right before the
 * « Intervenants » card (mahhp 22538), itself followed by the Documents card.
 */
export const ClaimTasksCardConfigurable = withComponentRules(
  ClaimTasksCard,
  'claimflow.claimTasksCard',
);

/**
 * Rule-aware wrapped `CloseCoverageCard`. Ships unused — pages keep
 * importing the raw component until a per-page swap is approved.
 */
export const CloseCoverageCardConfigurable = withComponentRules(
  CloseCoverageCard,
  'claimflow.closeCoverageCard',
);

/**
 * Rule-aware wrapped `CloseTaskCard`. Ships unused — pages keep
 * importing the raw component until a per-page swap is approved.
 */
export const CloseTaskCardConfigurable = withComponentRules(
  CloseTaskCard,
  'claimflow.closeTaskCard',
);

/**
 * Rule-aware wrapped `CostSynthesisCard`. `TaskDetailPage` imports it
 * (aliased as `CostSynthesisCard`); a `setProp` rule on `defaultShowPostes`
 * lets a tenant open the card with the poste detail rows expanded (mahhp
 * 22854).
 */
export const CostSynthesisCardConfigurable = withComponentRules(
  CostSynthesisCard,
  'claimflow.costSynthesisCard',
);

/**
 * Rule-aware wrapped `ComptabiliteCard` (mahhp 22853). `TaskDetailPage`
 * imports it (aliased as `ComptabiliteCard`) and renders it right under the
 * "Synthèse des coûts" card.
 */
export const ComptabiliteCardConfigurable = withComponentRules(
  ComptabiliteCard,
  'claimflow.comptabiliteCard',
);

/**
 * Rule-aware wrapped `CreateTaskCard`. Ships unused — pages keep
 * importing the raw component until a per-page swap is approved.
 */
export const CreateTaskCardConfigurable = withComponentRules(
  CreateTaskCard,
  'claimflow.createTaskCard',
);

/**
 * Rule-aware wrapped `DocumentsCard`. Ships unused — pages keep
 * importing the raw component until a per-page swap is approved.
 */
export const DocumentsCardConfigurable = withComponentRules(
  DocumentsCard,
  'claimflow.documentsCard',
);

/**
 * Rule-aware wrapped `DocumentPreview`.
 *
 * Re-exported from `./documents/DocumentPreview`, where it is **co-located**
 * with the raw component: `register.ts` imports `DocumentsCard`, and
 * `DocumentsCard` renders this wrapped preview, so building the HOC wrapper
 * here would create a register↔DocumentsCard import cycle (a broken cycle
 * makes the imported component `undefined` and the render throws). Building it
 * in the leaf `DocumentPreview` module avoids the cycle entirely; the
 * registration (id + raw component + schema) stays in this file. This re-export
 * keeps every claimflow `*Configurable` discoverable from the one barrel.
 */
export { DocumentPreviewConfigurable };

/**
 * Rule-aware wrapped `EmailView`.
 *
 * Re-exported from `./documents/EmailView`, where it is **co-located** with
 * the raw component: `register.ts` imports `DocumentsCard`, which renders
 * `EmailDetail`, which renders this wrapped view, so building the HOC wrapper
 * here would create a register↔DocumentsCard↔EmailDetail import cycle (a broken
 * cycle makes the imported component `undefined` and the render throws).
 * Building it in the leaf `EmailView` module avoids the cycle entirely; the
 * registration (id + raw component + schema) stays in this file. This re-export
 * keeps every claimflow `*Configurable` discoverable from the one barrel.
 */
export { EmailViewConfigurable };

/**
 * Rule-aware wrapped `IntervenantForm`. `TaskDetailPage` imports this
 * constant (aliased as `IntervenantForm`) so admin-authored rules fire on
 * every render — in particular, `setProp` rules on `allowedCategories`,
 * `defaultCategory`, `defaultExistant`, and `existantByCategory` let each
 * tenant restrict the Catégorie options, pin the initial category,
 * (paired with a `hideField existant` layout effect) freeze the intervenant
 * creation mode, and auto-(un)check Existant when a given category is
 * picked — all without a code deploy. See
 * `.claude-work/specs/intervenant-form.md` for the documented rule JSONs.
 */
export const IntervenantFormConfigurable = withComponentRules(
  IntervenantForm,
  'claimflow.intervenantForm',
);

/**
 * Rule-aware wrapped `EditIntervenantCard` (« Modifier un intervenant », mahhp
 * 22919). `TaskDetailPage` imports this constant (aliased as
 * `EditIntervenantCard`) so admin-authored rules — layout rules on its stable
 * field keys, the title and the save button in particular — fire on every
 * render without any JSX churn on the page itself.
 */
export const EditIntervenantCardConfigurable = withComponentRules(
  EditIntervenantCard,
  'claimflow.editIntervenantCard',
);

/**
 * Rule-aware wrapped `IntervenantsCard` (« Intervenants » table, mahhp 22538).
 * `TaskDetailPage` imports it (aliased as `IntervenantsCard`) and renders it
 * right under the « Tâches » card, before the Documents card, so layout rules
 * on its stable column keys, the pencil action and the title fire on every
 * render without any JSX churn on the page itself.
 */
export const IntervenantsCardConfigurable = withComponentRules(
  IntervenantsCard,
  'claimflow.intervenantsCard',
);

/**
 * Rule-aware wrapped `OuvertureSinistreCard`. `TaskDetailPage` imports
 * this constant (aliased as `OuvertureSinistreCard`) so admin-authored
 * rules fire on every render without any JSX churn on the page itself.
 */
export const OuvertureSinistreCardConfigurable = withComponentRules(
  OuvertureSinistreCard,
  'claimflow.ouvertureSinistreCard',
);

/**
 * Rule-aware wrapped `RequestPiecesCard`. `TaskDetailPage` imports this
 * constant (aliased as `RequestPiecesCard`) so admin-authored rules fire
 * on every render without any JSX churn on the page itself.
 */
export const RequestPiecesCardConfigurable = withComponentRules(
  RequestPiecesCard,
  'claimflow.requestPiecesCard',
);

/**
 * Rule-aware wrapped `MissingPiecesCard`. `TaskDetailPage` imports this
 * constant (aliased as `MissingPiecesCard`) so admin-authored rules fire
 * on every render without any JSX churn on the page itself.
 */
export const MissingPiecesCardConfigurable = withComponentRules(
  MissingPiecesCard,
  'claimflow.missingPiecesCard',
);

/**
 * Rule-aware wrapped `EditDocumentCard`. `TaskDetailPage` imports this
 * constant (aliased as `EditDocumentCard`) so admin-authored rules fire
 * on every render without any JSX churn on the page itself.
 */
export const EditDocumentCardConfigurable = withComponentRules(
  EditDocumentCard,
  'claimflow.editDocumentCard',
);

/**
 * Rule-aware wrapped `SettlementCard`. `TaskDetailPage` imports this
 * constant (aliased as `SettlementCard`) so admin-authored rules fire on
 * every render — in particular, `setProp` rules on the four `default*`
 * props let each tenant override the initial Mode de règlement, Banque,
 * Monnaie, and Nature opération selections without a code deploy. See
 * `.claude-work/specs/settlement-card.md` for the documented
 * `iga-servicing` rule JSONs.
 */
export const SettlementCardConfigurable = withComponentRules(
  SettlementCard,
  'claimflow.settlementCard',
);

/**
 * Rule-aware wrapped `RecoveryCard`. `TaskDetailPage` imports this
 * constant (aliased as `RecoveryCard`) so admin-authored rules fire on
 * every render — in particular, `setProp` rules on the four `default*`
 * props let each tenant override the initial Mode de règlement, Banque,
 * Monnaie, and Nature opération selections without a code deploy. When
 * `defaultNature` is unset, the card late-binds the Nature opération
 * from `paramReglement.CD_OP` once the per-claim selector resolves. A
 * `setProp` rule on `allowedNatures` (the tenant-tunable allow-list of
 * `CD_OP` codes) also limits which Nature opération options are offered.
 * See `.claude-work/specs/recovery-card.md` for the documented
 * `iga-servicing` rule JSONs.
 */
export const RecoveryCardConfigurable = withComponentRules(
  RecoveryCard,
  'claimflow.recoveryCard',
);

/**
 * Rule-aware wrapped `EmailCard`. `TaskDetailPage` imports this constant
 * (aliased as `EmailCard`) so admin-authored rules fire on every render
 * without any JSX churn on the page itself.
 */
export const EmailCardConfigurable = withComponentRules(
  EmailCard,
  'claimflow.emailCard',
);

/**
 * Rule-aware wrapped `MiseAJourDossierCard`. `TaskDetailPage` imports this
 * constant (aliased as `MiseAJourDossierCard`) so admin-authored rules fire
 * on every render without any JSX churn on the page itself.
 */
export const MiseAJourDossierCardConfigurable = withComponentRules(
  MiseAJourDossierCard,
  'claimflow.miseAJourDossierCard',
);

/**
 * Rule-aware wrapped `ClotureSinistreCard` (« Clôture », mahhp 22846).
 * `TaskDetailPage` imports this constant (aliased as `ClotureSinistreCard`)
 * and renders it right after the « Mise à jour dossier » card, so layout rules
 * on its stable `motif` field, the « Valider » button and the title fire on
 * every render without any JSX churn on the page itself. The page-wired
 * `onClaimClosed` callback flows straight through.
 */
export const ClotureSinistreCardConfigurable = withComponentRules(
  ClotureSinistreCard,
  'claimflow.clotureSinistreCard',
);

/**
 * Rule-aware wrapped `EstimationCard`. `TaskDetailPage` imports this
 * constant (aliased as `EstimationCard`) so admin-authored rules fire on
 * every render — in particular, a `setProp` rule on `deductionPostes` lets
 * each tenant choose which `CD_PSD` postes (e.g. Franchise + Valeur de
 * sauvetage) are subtracted from the Provision nette estimée without a code
 * deploy. See `.claude-work/specs/estimation-card.md` for the documented
 * `iga-servicing` rule JSON.
 */
export const EstimationCardConfigurable = withComponentRules(
  EstimationCard,
  'claimflow.estimationCard',
);

/**
 * Rule-aware wrapped `EstimationRecoursCard`. `TaskDetailPage` imports this
 * constant (aliased as `EstimationRecoursCard`) so admin-authored rules fire on
 * every render — in particular, a `setProp` rule on `deductionPostes` lets each
 * tenant choose which `CD_PSD` postes (e.g. Franchise + Valeur de sauvetage) are
 * subtracted from the « Recours estimé » total without a code deploy, exactly
 * like its `EstimationCard` twin.
 */
export const EstimationRecoursCardConfigurable = withComponentRules(
  EstimationRecoursCard,
  'claimflow.estimationRecoursCard',
);

/**
 * Rule-aware wrapped `OtherActionsPanel`. `TaskDetailPage` imports this constant
 * (aliased as `OtherActionsPanel`) so admin-authored layout rules (rename / hide
 * / reorder of the launcher rows, or a title override) fire on every render
 * without any JSX churn on the page itself. The HOC injects `_layoutOverrides`;
 * the page-wired `onSelect` callback flows straight through.
 */
export const OtherActionsPanelConfigurable = withComponentRules(
  OtherActionsPanel,
  'claimflow.otherActionsPanel',
);

/**
 * Rule-aware wrapped `ConfigHistoryCard`. `ConfigVersioningPage` imports this
 * constant (aliased as `ConfigHistoryCard`) so admin-authored layout rules
 * (rename / hide / reorder of the history columns, or a title override) fire on
 * every render without any JSX churn on the page itself.
 */
export const ConfigHistoryCardConfigurable = withComponentRules(
  ConfigHistoryCard,
  'claimflow.configHistoryCard',
);

/**
 * Rule-aware wrapped `ConfigBackupsCard`. `ConfigVersioningPage` imports this
 * constant (aliased as `ConfigBackupsCard`) so admin-authored layout rules fire
 * on every render without any JSX churn on the page itself.
 */
export const ConfigBackupsCardConfigurable = withComponentRules(
  ConfigBackupsCard,
  'claimflow.configBackupsCard',
);
